import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationTriangle, 
  faTimes, 
  faCalendarAlt, 
  faCheckCircle,
  faHourglassHalf,
  faTimesCircle,
  faEnvelope,
  faShieldAlt,
  faFileAlt,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { DuplicateWarning } from '../../types/submission';

interface DuplicateWarningModalProps {
  isOpen: boolean;
  duplicates: DuplicateWarning[];
  year: number;
  templateName: string;
  hasConformityDuplicates?: boolean;
  hasEmergencyContactDuplicates?: boolean;
  onClose: () => void;
  onProceed: (skipSuppliers: string[], skipConformity: string[], skipEmergencyContact: string[], skipMainTemplate?: string[]) => void;
  onCancel: () => void;
}

const DuplicateWarningModal: React.FC<DuplicateWarningModalProps> = ({
  isOpen,
  duplicates,
  year,
  templateName,
  hasConformityDuplicates = false,
  hasEmergencyContactDuplicates = false,
  onClose,
  onProceed,
  onCancel
}) => {
  // State for granular supplier/template selections
  const [skipSuppliers, setSkipSuppliers] = useState<Set<string>>(new Set());
  const [skipMainTemplateForSuppliers, setSkipMainTemplateForSuppliers] = useState<Set<string>>(new Set());
  const [skipConformityForSuppliers, setSkipConformityForSuppliers] = useState<Set<string>>(new Set());
  const [skipEmergencyContactForSuppliers, setSkipEmergencyContactForSuppliers] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const handleSupplierToggle = (supplierId: string) => {
    const newSkipped = new Set(skipSuppliers);
    if (newSkipped.has(supplierId)) {
      newSkipped.delete(supplierId);
    } else {
      newSkipped.add(supplierId);
      // When excluding entire supplier, also clear individual template skips
      setSkipMainTemplateForSuppliers(prev => {
        const updated = new Set(prev);
        updated.delete(supplierId);
        return updated;
      });
      setSkipConformityForSuppliers(prev => {
        const updated = new Set(prev);
        updated.delete(supplierId);
        return updated;
      });
      setSkipEmergencyContactForSuppliers(prev => {
        const updated = new Set(prev);
        updated.delete(supplierId);
        return updated;
      });
    }
    setSkipSuppliers(newSkipped);
  };

  const handleMainTemplateToggle = (supplierId: string) => {
    const newSkipped = new Set(skipMainTemplateForSuppliers);
    if (newSkipped.has(supplierId)) {
      newSkipped.delete(supplierId);
    } else {
      newSkipped.add(supplierId);
    }
    setSkipMainTemplateForSuppliers(newSkipped);
  };

  const handleConformityToggle = (supplierId: string) => {
    const newSkipped = new Set(skipConformityForSuppliers);
    if (newSkipped.has(supplierId)) {
      newSkipped.delete(supplierId);
    } else {
      newSkipped.add(supplierId);
    }
    setSkipConformityForSuppliers(newSkipped);
  };

  const handleEmergencyContactToggle = (supplierId: string) => {
    const newSkipped = new Set(skipEmergencyContactForSuppliers);
    if (newSkipped.has(supplierId)) {
      newSkipped.delete(supplierId);
    } else {
      newSkipped.add(supplierId);
    }
    setSkipEmergencyContactForSuppliers(newSkipped);
  };

  const handleProceed = () => {
    // Convert sets to arrays and merge logic
    const skipSuppliersArray = Array.from(skipSuppliers);
    const skipConformityArray = Array.from(skipConformityForSuppliers);
    const skipEmergencyContactArray = Array.from(skipEmergencyContactForSuppliers);
    const skipMainTemplateArray = Array.from(skipMainTemplateForSuppliers);
    
    onProceed(skipSuppliersArray, skipConformityArray, skipEmergencyContactArray, skipMainTemplateArray);
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'completed':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
      case 'in_progress':
        return <FontAwesomeIcon icon={faHourglassHalf} className="text-yellow-500" />;
      case 'sent':
        return <FontAwesomeIcon icon={faEnvelope} className="text-blue-500" />;
      case 'overdue':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />;
      default:
        return <FontAwesomeIcon icon={faHourglassHalf} className="text-gray-500" />;
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'Abgeschlossen';
      case 'in_progress':
        return 'In Bearbeitung';
      case 'sent':
        return 'Versendet';
      case 'overdue':
        return 'Überfällig';
      case 'draft':
        return 'Entwurf';
      default:
        return status || 'Unbekannt';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unbekannt';
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle} 
                  className="text-yellow-600 text-lg" 
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Duplikate gefunden
                </h3>
                <p className="text-sm text-gray-600">
                  {duplicates.length} Lieferant(en) haben bereits Submissions für "{templateName}" in {year}
                  {(() => {
                    const addOns = [];
                    if (hasConformityDuplicates) addOns.push('Konformitätserklärungen');
                    if (hasEmergencyContactDuplicates) addOns.push('Notfallkontakte');
                    return addOns.length > 0 ? ` (einschließlich ${addOns.join(' und ')})` : '';
                  })()}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="mb-4">
            <p className="text-gray-700 mb-4">
              Die folgenden Lieferanten haben bereits Fragebögen erhalten. 
              Sie können granular entscheiden, was übersprungen werden soll:
            </p>
          </div>

          {/* Duplicates List */}
          <div className="space-y-4">
            {duplicates.map((duplicate, index) => (
              <div
                key={`${duplicate.supplierId}-${index}`}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                {/* Supplier Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <h4 className="font-medium text-gray-900">
                      {duplicate.supplierName}
                    </h4>
                    <span className="text-sm text-gray-500">
                      {duplicate.supplierEmail}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={skipSuppliers.has(duplicate.supplierId)}
                        onChange={() => handleSupplierToggle(duplicate.supplierId)}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-red-600 font-medium">Lieferant ausschließen</span>
                    </label>
                  </div>
                </div>

                {/* Templates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Main Template */}
                  {duplicate.mainTemplate && (
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faFileAlt} className="text-blue-500" />
                          <span className="font-medium text-sm">Hauptfragebogen</span>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(duplicate.mainTemplate.status)}
                            <span className="text-xs">{getStatusText(duplicate.mainTemplate.status)}</span>
                          </div>
                        </div>
                        
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={skipMainTemplateForSuppliers.has(duplicate.supplierId)}
                            onChange={() => handleMainTemplateToggle(duplicate.supplierId)}
                            disabled={skipSuppliers.has(duplicate.supplierId)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <span className="text-xs text-blue-600">Überspringen</span>
                        </label>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Deadline: {formatDate(duplicate.mainTemplate.deadline)}</div>
                        <div>Erstellt: {formatDate(duplicate.mainTemplate.createdAt)}</div>
                      </div>
                    </div>
                  )}

                  {/* Conformity Template */}
                  {duplicate.conformityTemplate && (
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faShieldAlt} className="text-green-500" />
                          <span className="font-medium text-sm">Konformitätserklärung</span>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(duplicate.conformityTemplate.status)}
                            <span className="text-xs">{getStatusText(duplicate.conformityTemplate.status)}</span>
                          </div>
                        </div>
                        
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={skipConformityForSuppliers.has(duplicate.supplierId)}
                            onChange={() => handleConformityToggle(duplicate.supplierId)}
                            disabled={skipSuppliers.has(duplicate.supplierId)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:opacity-50"
                          />
                          <span className="text-xs text-orange-600">Überspringen</span>
                        </label>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Deadline: {formatDate(duplicate.conformityTemplate.deadline)}</div>
                        <div>Erstellt: {formatDate(duplicate.conformityTemplate.createdAt)}</div>
                      </div>
                    </div>
                  )}

                  {/* Emergency Contact Template */}
                  {duplicate.emergencyContactTemplate && (
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faPhone} className="text-red-500" />
                          <span className="font-medium text-sm">Notfallkontakt</span>
                          <div className="flex items-center space-x-1">
                            {getStatusIcon(duplicate.emergencyContactTemplate.status)}
                            <span className="text-xs">{getStatusText(duplicate.emergencyContactTemplate.status)}</span>
                          </div>
                        </div>
                        
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={skipEmergencyContactForSuppliers.has(duplicate.supplierId)}
                            onChange={() => handleEmergencyContactToggle(duplicate.supplierId)}
                            disabled={skipSuppliers.has(duplicate.supplierId)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:opacity-50"
                          />
                          <span className="text-xs text-orange-600">Überspringen</span>
                        </label>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Deadline: {formatDate(duplicate.emergencyContactTemplate.deadline)}</div>
                        <div>Erstellt: {formatDate(duplicate.emergencyContactTemplate.createdAt)}</div>
                      </div>
                    </div>
                  )}

                  {/* Legacy Support */}
                  {!duplicate.mainTemplate && !duplicate.conformityTemplate && duplicate.status && (
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <FontAwesomeIcon icon={faFileAlt} className="text-blue-500" />
                        <span className="font-medium text-sm">Template</span>
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(duplicate.status)}
                          <span className="text-xs">{getStatusText(duplicate.status)}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Deadline: {formatDate(duplicate.deadline)}</div>
                        <div>Erstellt: {formatDate(duplicate.createdAt)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-yellow-500" />
              {skipSuppliers.size > 0 && `${skipSuppliers.size} Lieferant(en) werden komplett ausgeschlossen. `}
              {skipMainTemplateForSuppliers.size > 0 && `${skipMainTemplateForSuppliers.size} Hauptfragebogen werden übersprungen. `}
              {skipConformityForSuppliers.size > 0 && `${skipConformityForSuppliers.size} Konformitätserklärung(en) werden übersprungen. `}
              {skipEmergencyContactForSuppliers.size > 0 && `${skipEmergencyContactForSuppliers.size} Notfallkontakt(e) werden übersprungen. `}
              {skipSuppliers.size === 0 && skipMainTemplateForSuppliers.size === 0 && skipConformityForSuppliers.size === 0 && skipEmergencyContactForSuppliers.size === 0 && 
                "Alle ausgewählten Lieferanten erhalten neue Submissions für alle Template-Typen."}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleProceed}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
              >
                Senden
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DuplicateWarningModal;
